import React, { useState, useEffect, useRef } from 'react';
import {
  Paper,
  Typography,
  Grid,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Box
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import DownloadIcon from '@mui/icons-material/Download';
import CollapsibleSection from '../../../../../components/common/CollapsibleSection';
import { StudioConfig } from '../../../../../types/studio.types';
import { GetMusic, Music } from '../../../api-studio';

interface MusicSectionProps {
  studioConfig: StudioConfig;
  isLoading: boolean;
  onTextChange: (field: string, value: string | string[]) => void;
  icon?: React.ReactNode;
  websiteId: string;
}

const MusicSection: React.FC<MusicSectionProps> = ({
  studioConfig,
  isLoading,
  onTextChange,
  icon,
  websiteId
}) => {
  const [musicTracks, setMusicTracks] = useState<Music[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Helper to format runtime seconds into mm:ss
  const formatRuntime = (seconds?: number) => {
    if (typeof seconds !== 'number' || !isFinite(seconds)) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const mm = String(mins).padStart(2, '0');
    const ss = String(secs).padStart(2, '0');
    return `${mm}:${ss}`;
  };

  useEffect(() => {
    const fetchMusic = async () => {
      if (!websiteId) return;
      
      setLoading(true);
      try {
        const tracks = await GetMusic(websiteId);
        // Keep all tracks; category-specific filtering is applied per table
        setMusicTracks(tracks);
      } catch (error) {
        console.error("Failed to fetch music tracks:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchMusic();
  }, [websiteId]);

  const handleIntroSelect = (trackId: string) => {
    const currentSelections = studioConfig.introMusicIds || [];
    let newSelections: string[];
    
    if (currentSelections.includes(trackId)) {
      newSelections = currentSelections.filter(id => id !== trackId);
    } else {
      newSelections = [...currentSelections, trackId];
    }
    
    onTextChange('introMusicIds', newSelections);
  };

  const handleOutroSelect = (trackId: string) => {
    const currentSelections = studioConfig.outroMusicIds || [];
    let newSelections: string[];
    
    if (currentSelections.includes(trackId)) {
      newSelections = currentSelections.filter(id => id !== trackId);
    } else {
      newSelections = [...currentSelections, trackId];
    }
    
    onTextChange('outroMusicIds', newSelections);
  };

  const handleBetweenStoriesSelect = (trackId: string) => {
    const currentSelections = studioConfig.betweenStoriesSoundEffectIds || [];
    let newSelections: string[];

    if (currentSelections.includes(trackId)) {
      newSelections = currentSelections.filter(id => id !== trackId);
    } else {
      newSelections = [...currentSelections, trackId];
    }

    onTextChange('betweenStoriesSoundEffectIds', newSelections);
  };

  const handlePlayMusic = (url: string, trackId: string) => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    
    if (currentlyPlaying === trackId) {
      setCurrentlyPlaying(null);
      return;
    }
    
    const audio = new Audio(url);
    audio.play();
    audioRef.current = audio;
    setCurrentlyPlaying(trackId);
    
    audio.onended = () => {
      setCurrentlyPlaying(null);
      audioRef.current = null;
    };
  };

  const handleDownloadMusic = (url: string, trackName: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = `${trackName}.mp3`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderMusicTable = (
    title: string,
    tracks: Music[] = [],
    selectionConfig: {
      label: string;
      selectedIds: string[];
      onSelect: (trackId: string) => void;
    }[]
  ) => (
    <TableContainer component={Paper} variant="outlined">
      <Typography variant="h6" sx={{ p: 2 }}>
        {title}
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            {selectionConfig.map((config, index) => (
              <TableCell key={index} padding="checkbox">{config.label}</TableCell>
            ))}
            <TableCell>Name</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Keywords</TableCell>
            <TableCell>Runtime</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tracks.map((track) => (
            <TableRow key={track.id}>
              {selectionConfig.map((config, index) => (
                <TableCell key={index} padding="checkbox">
                  <Checkbox
                    checked={config.selectedIds.includes(track.id)}
                    onChange={() => config.onSelect(track.id)}
                    disabled={isLoading}
                  />
                </TableCell>
              ))}
              <TableCell>{track.name}</TableCell>
              <TableCell>{track.description}</TableCell>
              <TableCell>{track.keywords}</TableCell>
              <TableCell>{formatRuntime(track.runtime)}</TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <IconButton 
                    onClick={() => handleDownloadMusic(track.signedUrl, track.name)}
                    disabled={isLoading}
                    size="small"
                    title="Download"
                  >
                    <DownloadIcon />
                  </IconButton>
                  <IconButton 
                    onClick={() => handlePlayMusic(track.signedUrl, track.id)}
                    disabled={isLoading}
                    size="small"
                    title="Play/Pause"
                  >
                    {currentlyPlaying === track.id ? <PauseIcon /> : <PlayArrowIcon />}
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>
          ))}
          {tracks.length === 0 && !loading && (
            <TableRow>
              <TableCell colSpan={5 + selectionConfig.length} align="center">
                No music tracks available
              </TableCell>
            </TableRow>
          )}
          {loading && (
            <TableRow>
              <TableCell colSpan={5 + selectionConfig.length} align="center">
                <CircularProgress size={20} />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const standardTracks = musicTracks.filter(t => t.category !== 'betweenstories');
  const betweenStoriesTracks = musicTracks.filter(t => t.category === 'betweenstories');

  return (
    <CollapsibleSection title="Music" icon={icon}>
      <Typography variant="body2" color="text.secondary" paragraph>
        Select intro and outro music for your audio content. You can choose multiple tracks, and one will be randomly selected for each audio segment.
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          {renderMusicTable(
            "Intro & Outro Music",
            standardTracks,
            [
              { label: "Intro", selectedIds: studioConfig.introMusicIds || [], onSelect: handleIntroSelect },
              { label: "Outro", selectedIds: studioConfig.outroMusicIds || [], onSelect: handleOutroSelect }
            ]
          )}
        </Grid>
        {/* New table below Intro and Outro */}
        <Grid item xs={12}>
          {renderMusicTable(
            "Sound Effect Between Stories",
            betweenStoriesTracks,
            [
              { label: "Select", selectedIds: studioConfig.betweenStoriesSoundEffectIds || [], onSelect: handleBetweenStoriesSelect }
            ]
          )}
        </Grid>
      </Grid>
      
      {/* Audio element for playback control but hidden from view */}
      <Box sx={{ display: 'none' }}>
        <audio ref={audioRef} />
      </Box>
    </CollapsibleSection>
  );
};

export default MusicSection;